import Alert from '@mui/material/Alert';
import type { TQuizQuestion } from '@onesource/services';
import type { TSubmitAnswer } from '@onesource/schemas';
import { MultipleChoiceQuestion } from './multiple-choice-question';
import { MultiSelectQuestion } from './multi-select-question';
import { TrueFalseQuestion } from './true-false-question';
import { ShortAnswerQuestion } from './short-answer-question';
import { OrderingQuestion } from './ordering-question';

interface QuestionRendererProps {
  question: TQuizQuestion;
  answer: TSubmitAnswer;
  onAnswerChange: (answer: TSubmitAnswer) => void;
  disabled?: boolean;
}

/**
 * Question Renderer Component
 * Routes to the appropriate question type component based on questionType
 */
export const QuestionRenderer = ({ question, answer, onAnswerChange, disabled = false }: QuestionRendererProps) => {
  const handleOptionSelect = (optionIds: string[]) => {
    onAnswerChange({
      ...answer,
      selectedOptionIds: optionIds,
    });
  };

  const handleTextChange = (text: string) => {
    onAnswerChange({
      ...answer,
      textAnswer: text,
    });
  };

  const handleOrderChange = (sequence: string[]) => {
    onAnswerChange({
      ...answer,
      orderingSequence: sequence,
    });
  };

  switch (question.questionType) {
    case 'multiple_choice': {
      return (
        <MultipleChoiceQuestion
          question={question}
          selectedOptionIds={answer.selectedOptionIds || []}
          onSelect={handleOptionSelect}
          disabled={disabled}
        />
      );
    }
    case 'multiple_select': {
      return (
        <MultiSelectQuestion
          question={question}
          selectedOptionIds={answer.selectedOptionIds || []}
          onSelect={handleOptionSelect}
          disabled={disabled}
        />
      );
    }
    case 'true_false': {
      return (
        <TrueFalseQuestion
          question={question}
          selectedOptionIds={answer.selectedOptionIds || []}
          onSelect={handleOptionSelect}
          disabled={disabled}
        />
      );
    }
    case 'short_answer': {
      return (
        <ShortAnswerQuestion
          question={question}
          textAnswer={answer.textAnswer || ''}
          onTextChange={handleTextChange}
          disabled={disabled}
        />
      );
    }
    case 'ordering': {
      return (
        <OrderingQuestion
          question={question}
          orderingSequence={answer.orderingSequence || []}
          onOrderChange={handleOrderChange}
          disabled={disabled}
        />
      );
    }
    default: {
      return <Alert severity="warning">Unknown question type: {question.questionType}</Alert>;
    }
  }
};
